import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import ReportModule from '../ReportModule';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear, faGears, faSave } from '@fortawesome/free-solid-svg-icons';
import { useUser } from '../../../UserContext';
import {
    faDna,
    faHeartPulse,
    faDroplet,
    faSyringe,
    faStar,
    faEye,
    faEyeSlash,
    faPills,
    faSpinner,
    faFilter,   
    faTable,
    faSearch,
    faCheck,
    faChevronUp,
    faChevronDown,
    faFileDownload,
    faCheckCircle,
    faPen, faTrash, faTimes} from '@fortawesome/free-solid-svg-icons';

const ITEMS_PER_LOAD = 50;
const SCROLL_THRESHOLD = 150;
      
const CLINICIAN_PANEL_OPTIONS = [
    { group: "Health Risk", options: ["Health Risk (Very Important)", "Health Risk (Important)", "Health Risk (Noteworthy)"] },
    { group: "Carrier", options: ["Carrier (Important)", "Carrier (Noteworthy)"] },
    { group: "Other", options: ["Clinician Choice"] }
];
const GenomicsReport = ({ id, type, data = {} }) => {
    const { user } = useUser();
    const [reportData, setReportData] = useState({
        variantReport: [],
        drugData: [],
        traits: []
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('genome');
    const [expandedItems, setExpandedItems] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('Whole Genome');
    const [popupVisible, setPopupVisible] = useState(false);
    const [geneSearchTerm, setGeneSearchTerm] = useState('');
    const [selectedGenes, setSelectedGenes] = useState([]);
    const [temporaryFilterActive, setTemporaryFilterActive] = useState(false);
    const [temporarySelectedGenes, setTemporarySelectedGenes] = useState([]);
    const [isFilterLocked, setIsFilterLocked] = useState(false);
    const [activeFilterTab, setActiveFilterTab] = useState('genes');
    const [selectedTemplateId, setSelectedTemplateId] = useState(null)
    const [modalVisible, setModalVisible] = useState(false);
    const [modalType, setModalType] = useState('');           // 'variant' or 'hpo'
    const [modalData, setModalData] = useState(null);         // will hold either the variant object or the hpoMatches array
    
    const [parameterFilters, setParameterFilters] = useState({
        ClinVar_CLNSIG: [],
        ReviewStar: [],
        SZAreportCategory: { min: 0, max: 15 },
        IMPACT: [],
        MAX_AF: { min: 0, max: 1 },
        am_class: [],
        ada_score: { min: 0.6, max: 1 },
        rf_score: { min: 0.6, max: 1 },
        SpliceAI_pred_DS_AL: { min: 0.5, max: 1 },
        SpliceAI_pred_DS_DG: { min: 0.5, max: 1 },
        SpliceAI_pred_DS_DL: { min: 0.5, max: 1 }
    });
    const [appliedParameterFilters, setAppliedParameterFilters] = useState({});
    const [temporaryParameterFilters, setTemporaryParameterFilters] = useState({});

    // incremental rendering
    const [renderedCounts, setRenderedCounts] = useState({
        genome: ITEMS_PER_LOAD,
        drugs: ITEMS_PER_LOAD,
        traits: ITEMS_PER_LOAD
    });
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const contentRef = useRef(null);

    // template management states
    const [templates, setTemplates] = useState([]);
    const [newTemplateName, setNewTemplateName] = useState('');
    const [nameError, setNameError] = useState(false);
    const [renamingTemplateId, setRenamingTemplateId] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [advancedMenuVisible, setAdvancedMenuVisible] = useState(false);
    const [panelDropdownPosition, setPanelDropdownPosition] = useState({ top: 0, left: 0 });

    // fetch data
    useEffect(() => {
        const fetchGenomicData = async () => {
            try {
                setIsLoading(true);
                setError(null);
                setRenderedCounts({ genome: ITEMS_PER_LOAD, drugs: ITEMS_PER_LOAD, traits: ITEMS_PER_LOAD });
                const patientID = data.patientId || id;
                if (!patientID) throw new Error('No patient ID provided');
                const response = await fetch(`https://service1.szapfs.org/api/generiskpro/genomic-data/${patientID}`);
                if (!response.ok) throw new Error('Failed to fetch genomic data');
                const jsonData = await response.json();
                const processedVariantReport = (jsonData.variantReport || []).map(variant => {
                    let parsedClinCData = { selections: [] }; // Default to an object with an empty selections array
                    if (variant.ClinC && typeof variant.ClinC === 'string') {
                        try {
                            const parsed = JSON.parse(variant.ClinC);
                            // Ensure it has the 'selections' property and it's an array
                            if (parsed && Array.isArray(parsed.selections)) {
                                parsedClinCData = parsed;
                            } else {
                                // Potentially handle older single string format if necessary, or log warning
                                console.warn(`Parsed ClinC JSON for variant has unexpected structure (expected {selections: []}): ${variant.ClinC}`);
                            }
                        } catch (e) {
                            console.warn(`Failed to parse ClinC JSON for variant: ${variant.ClinC}`, e);
                        }
                    } else if (variant.ClinC && typeof variant.ClinC === 'object' && Array.isArray(variant.ClinC.selections)) {
                        parsedClinCData = variant.ClinC; // Already in the correct object format
                    }
                    return { ...variant, ClinC: parsedClinCData };
                });
                setReportData({
                    variantReport: processedVariantReport,
                    drugData: jsonData.drugData || [],
                    traits: jsonData.traits || []
                });
            } catch (err) {
                setError(err.message);
                console.error("Error fetching genomic data:", err);
                setReportData({ variantReport: [], drugData: [], traits: [] });
            } finally {
                setIsLoading(false);
            }
        };
        fetchGenomicData();
    }, [id, data.patientId]);

    // fetch templates whenever modal opens
    useEffect(() => {
        if (!popupVisible) return;
        const loadTemplates = async () => {
            try {
                const res = await fetch(`https://service1.szapfs.org/api/genomic-templates/${user.id}`);
                const list = await res.json();
                setTemplates(list);
            } catch (err) {
                console.error("Error loading templates:", err);
            }
        };
        loadTemplates();
    }, [popupVisible, user.id]);

    // handlers for toggle, expand, filter clear/apply remain unchanged...


    const togglePopup = () => {
        setPopupVisible(vis => {
            if (!vis) {
                // reset temporary
                setTemporarySelectedGenes([...selectedGenes]);
                setTemporaryParameterFilters({ ...appliedParameterFilters });
                setGeneSearchTerm('');
                setActiveFilterTab('genes');
                setNewTemplateName('');
                setAdvancedMenuVisible(false);
                setRenamingTemplateId(null);
            }
            return !vis;
        });
    };

    // save as template
    const handleSaveTemplate = async () => {
        if (!newTemplateName.trim()) {
            setNameError(true);
            setTimeout(() => setNameError(false), 2000);
            return;
        }
        const payload = {
            userId: user.id,
            templateName: newTemplateName.trim(),
            templateType: 'Filter',
            templateData: {
                genes: temporarySelectedGenes,
                parameters: temporaryParameterFilters
            }
        };
        try {
            const res = await fetch('https://service1.szapfs.org/api/genomic-templates', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error();
            setNewTemplateName('');
            const created = await res.json();
            setTemplates([{ id: created.id, template_name: newTemplateName, templateData: payload.templateData }, ...templates]);
        } catch (err) {
            console.error("Error saving template:", err);
            alert("Could not save");
        }
    };
    const [openPanelSelector, setOpenPanelSelector] = useState(null); // Stores the unique key of the open selector

    const applyTemplate = tpl => {
        setTemporarySelectedGenes(tpl.templateData.genes || []);
        setTemporaryParameterFilters(tpl.templateData.parameters || {});
    };

    const startRename = tpl => {
        setRenamingTemplateId(tpl.id);
        setRenameValue(tpl.template_name);
    };

    const confirmRename = async () => {
        try {
            await fetch(`https://service1.szapfs.org/api/genomic-templates/${renamingTemplateId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ templateName: renameValue.trim() })
            });
            setTemplates(tpls =>
                tpls.map(t => t.id === renamingTemplateId ? { ...t, template_name: renameValue.trim() } : t)
            );
            setRenamingTemplateId(null);
            setRenameValue('');
        } catch (err) {
            console.error("Rename failed:", err);
            alert("Rename failed");
        }
    };

    const deleteTemplate = async (idToDel) => {
        try {
          await fetch(`https://service1.szapfs.org/api/genomic-templates/${idToDel}`, {
            method: 'DELETE'
          });
          setTemplates((tpls) => tpls.filter((t) => t.id !== idToDel));
        } catch (err) {
          console.error("Delete failed:", err);
          alert("Delete failed");
        }
      };


    // --- Item Interaction Handlers ---
    const handleClinCPanelToggle = async (variant, panelNameToToggle) => {
        const originalClinCData = variant.ClinC; // For reverting on error
        const currentSelections = Array.isArray(variant.ClinC?.selections) ? [...variant.ClinC.selections] : [];

        let newSelections;
        if (currentSelections.includes(panelNameToToggle)) {
            newSelections = currentSelections.filter(p => p !== panelNameToToggle);
        } else {
            newSelections = [...currentSelections, panelNameToToggle];
        }

        const updatedClinCDataObject = newSelections.length > 0 ? { selections: newSelections } : { selections: [] }; // Store empty array if nothing selected for consistency
        const updatedVariant = { ...variant, ClinC: updatedClinCDataObject };
        
        const variantMatcher = (v, targetVariant) => (
            v.Genes === targetVariant.Genes &&
            v.Disease === targetVariant.Disease &&
            v.final_target_group === targetVariant.final_target_group &&
            v.index === targetVariant.index
        );

        setReportData(prev => ({
            ...prev,
            variantReport: prev.variantReport?.map(v =>
                variantMatcher(v, variant) ? updatedVariant : v
            )
        }));

        try {
            const payloadVariantData = {
                Genes: variant.Genes,
                Disease: variant.Disease,
                final_target_group: variant.final_target_group,
                index: variant.index
            };

            const payload = {
                sampleId: data.sampleId || id,
                variantData: payloadVariantData,
                clinCSelections: newSelections // Send the array of selected panel names
            };
            
            const response = await fetch('https://service1.szapfs.org/api/genomic-clinician-choice', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(`Failed to update ClinC panels: ${response.statusText} - ${errorData.error || ''}`);
            }

            await response.json();

        } catch (error) {
            console.error('Error updating ClinC panel selections:', error);
            setReportData(prev => ({
                ...prev,
                variantReport: prev.variantReport?.map(v =>
                    variantMatcher(v, variant) ? { ...variant, ClinC: originalClinCData } : v
                )
            }));
            alert(`Failed to update Clinician Panels: ${error.message}`);
        }
    };
    
    const handleToggleFilter = async (e, item, type, newState) => {
        e.stopPropagation();
        const toggleState = newState ? 1 : 0;
        const originalState = item.REPORT_TAG;

        // Optimistic UI update
        const updateItemState = (dataArray, itemToUpdate, state) => {
             return dataArray.map(i => i === itemToUpdate ? { ...i, REPORT_TAG: state } : i);
        };

        const revertState = () => {
            setReportData(prev => {
                 if (type === 'Disease') return { ...prev, variantReport: updateItemState(prev.variantReport, item, originalState) };
                 if (type === 'Drug') return { ...prev, drugData: updateItemState(prev.drugData, item, originalState) };
                 if (type === 'Trait') return { ...prev, traits: updateItemState(prev.traits, item, originalState) };
                 return prev;
            });
        };

        setReportData(prev => {
            if (type === 'Disease') return { ...prev, variantReport: updateItemState(prev.variantReport, item, toggleState) };
            if (type === 'Drug') return { ...prev, drugData: updateItemState(prev.drugData, item, toggleState) };
            if (type === 'Trait') return { ...prev, traits: updateItemState(prev.traits, item, toggleState) };
            return prev;
        });


        try {
            console.log('Sending toggle request:', { type, data: item, state: toggleState });
            const response = await fetch('https://service1.szapfs.org/api/GenomicsToggle', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sampleID: data.patientId,
                    type,
                    data: item,
                    state: toggleState
                })
            });

            if (!response.ok) {
                 // If the API call fails, revert the optimistic update
                revertState();
                throw new Error('Failed to toggle genomic filter');
            }
            // Success, UI is already updated
        } catch (error) {
            console.error('Error toggling genomic filter:', error);
            // Revert UI on error if not already reverted
            revertState();
            alert('Failed to update filter state. Please try again.');
        }
    };

    const toggleExpand = (itemId) => {
        setExpandedItems(prev => {
             // If the clicked item is already open, close all
             if (prev[itemId]) {
                 return {};
             }
             // Otherwise, close all others and open the clicked one
             return { [itemId]: true };
        });
    };
// Place this alongside your other helpers:
// 1) Add this somewhere after your useCallback filters:

// Returns true if `item` passes BOTH gene & parameter temp filters.
const matchesTempFilters = item => {
    const geneActive = temporarySelectedGenes.length > 0;
    const paramActive = Object.keys(temporaryParameterFilters).length > 0;
  
    let geneMatch = true;
    let paramMatch = true;
  
    if (geneActive) {
      geneMatch = filterDiseasesByGenes([item], temporarySelectedGenes).length > 0;
    }
    if (paramActive) {
      paramMatch = filterDiseasesByParameters([item], temporaryParameterFilters).length > 0;
    }
  
    return geneMatch && paramMatch;
  };
  
  useEffect(() => {
    const contentElement = contentRef.current;

    const handleCloseOnScroll = () => {
        if (openPanelSelector) {
            setOpenPanelSelector(null);
        }
    };

    if (contentElement && openPanelSelector) {
        contentElement.addEventListener('scroll', handleCloseOnScroll);
    }

    return () => {
        if (contentElement) {
            contentElement.removeEventListener('scroll', handleCloseOnScroll);
        }
    };
}, [openPanelSelector, contentRef]); // Dependencies: re-run if dropdown opens/closes or contentRef changes
    // --- Filtering Logic ---

    const filterDiseasesByParameters = useCallback((diseases, filters) => {
        if (!filters || Object.keys(filters).length === 0) return diseases;

        return diseases.filter(disease => {
            for (const [param, value] of Object.entries(filters)) {
                if (!disease.hasOwnProperty(param)) continue; // Skip if disease doesn't have the parameter

                const diseaseValue = disease[param];

                // Handle categorical filters
                if (Array.isArray(value)) {
                    if (value.length === 0) continue;

                    let matchValue = String(diseaseValue);
                     if (param === 'ClinVar_CLNSIG' && typeof diseaseValue === 'string') {
                         matchValue = diseaseValue.split('/')[0]; // Get first value before "/"
                     }

                    if (!value.includes(matchValue)) return false;
                }
                // Handle numerical ranges
                else if (typeof value === 'object' && value !== null && typeof value.min !== 'undefined' && typeof value.max !== 'undefined') {
                    const numValue = parseFloat(diseaseValue);
                    if (isNaN(numValue)) continue; // Skip if not a valid number
                    if (numValue < value.min || numValue > value.max) return false;
                }
            }
            return true;
        });
    }, []); // No dependencies needed as it's a pure function based on inputs

    const filterDiseasesByGenes = useCallback((diseases, genes) => {
        if (!genes || genes.length === 0) return diseases;
        const geneSet = new Set(genes); // Use Set for faster lookups

        return diseases.filter(disease => {
            const diseaseGenes = disease.Genes?.split(',').map(g => g.trim());
            if (!diseaseGenes || diseaseGenes.length === 0) return false;
            return diseaseGenes.some(diseaseGene => geneSet.has(diseaseGene));
        });
    }, []); // No dependencies needed


    // Memoized calculation of filtered data for each tab
    const filteredGenomeData = useMemo(() => {
        if (!reportData.variantReport) return [];
        let filtered = reportData.variantReport;

        if (temporaryFilterActive || isFilterLocked) {
            if (selectedGenes.length > 0) {
                filtered = filterDiseasesByGenes(filtered, selectedGenes);
            }
            if (Object.keys(appliedParameterFilters).length > 0) {
                filtered = filterDiseasesByParameters(filtered, appliedParameterFilters);
            }
        } else {
            switch (selectedFilter) {
                case 'Health Risks':
                    filtered = filtered.filter(d => d.final_target_group?.includes('Health'));
                    break;
                case 'Newborn':
                    filtered = filtered.filter(d => d.final_target_group?.includes('Newborn'));
                    break;
                case 'Carrier':
                    filtered = filtered.filter(d => d.final_target_group?.includes('Newborn') || d.final_target_group?.includes('Carrier'));
                    break;
                case 'Cancer':
                    filtered = filtered.filter(d => d.final_target_group?.includes('cancer'));
                    break;
                // 'Whole Genome' - no additional filtering
            }
        }

        if (searchTerm) {
            const termLower = searchTerm.toLowerCase();
            filtered = filtered.filter(item => {
              // match on any top‑level field
              const baseMatch = Object.values(item).some(val =>
                typeof val === 'string' && val.toLowerCase().includes(termLower)
              );
              if (baseMatch) return true;
              // match inside any HPO row
              return Array.isArray(item.hpoMatches) && item.hpoMatches.some(hpo =>
                Object.values(hpo).some(val =>
                  typeof val === 'string' && val.toLowerCase().includes(termLower)
                )
              );
            });
          }
          
        return filtered;
    }, [reportData.variantReport, searchTerm, selectedFilter, temporaryFilterActive, isFilterLocked, selectedGenes, appliedParameterFilters, filterDiseasesByGenes, filterDiseasesByParameters]);

    const filteredDrugData = useMemo(() => {
        if (!reportData.drugData) return [];
        let filtered = reportData.drugData;

        if (searchTerm) {
            const termLower = searchTerm.toLowerCase();
            filtered = filtered.filter(d =>
                Object.values(d).some(value =>
                    typeof value === 'string' && value.toLowerCase().includes(termLower)
                )
            );
        }
        return filtered;
    }, [reportData.drugData, searchTerm]);

    const filteredTraitsData = useMemo(() => {
        if (!reportData.traits) return [];
        let filtered = reportData.traits;

        if (searchTerm) {
            const termLower = searchTerm.toLowerCase();
            filtered = filtered.filter(d =>
                Object.values(d).some(value =>
                    typeof value === 'string' && value.toLowerCase().includes(termLower)
                )
            );
        }
        return filtered;
    }, [reportData.traits, searchTerm]);

    // --- Filter Popup Handlers ---


    const handleClearFilters = () => {
        setTemporarySelectedGenes([]);
        setTemporaryParameterFilters({});
        setSelectedGenes([]);
        setAppliedParameterFilters({});
        setTemporaryFilterActive(false);
        setIsFilterLocked(false);
        setPopupVisible(false);
        setRenderedCounts({ genome: ITEMS_PER_LOAD, drugs: ITEMS_PER_LOAD, traits: ITEMS_PER_LOAD });
        if (contentRef.current) contentRef.current.scrollTop = 0;
      
        // ** drop the selected template **
        setSelectedTemplateId(null);
      };

      const handleApplyGenes = () => {
        // ** any manual apply should clear the template highlight **
        setSelectedTemplateId(null);
      
        const genesChanged = JSON.stringify(temporarySelectedGenes) !== JSON.stringify(selectedGenes);
        const paramsChanged = JSON.stringify(temporaryParameterFilters) !== JSON.stringify(appliedParameterFilters);
      
        if (temporarySelectedGenes.length === 0 && Object.keys(temporaryParameterFilters).length === 0) {
          handleClearFilters();
          return;
        }
      
        setSelectedGenes(temporarySelectedGenes);
        setAppliedParameterFilters({ ...temporaryParameterFilters });
        const newFilterActive = temporarySelectedGenes.length > 0 || Object.keys(temporaryParameterFilters).length > 0;
        setTemporaryFilterActive(newFilterActive);
        setPopupVisible(false);
      
        if (newFilterActive) {
          setSelectedFilter('Whole Genome');
          setIsFilterLocked(true);
          if (genesChanged || paramsChanged) {
            setRenderedCounts(prev => ({ ...prev, genome: ITEMS_PER_LOAD }));
            if (contentRef.current) contentRef.current.scrollTop = 0;
          }
        } else {
          setIsFilterLocked(false);
        }
      };
      useEffect(() => {
        const handleClickOutside = (event) => {
            if (openPanelSelector) {
                const portalDropdown = document.getElementById(`panel-dropdown-portal-${openPanelSelector}`);
                const buttonThatOpenedIt = document.querySelector(`.clinician-panel-selector-button-inline[aria-controls="panel-dropdown-portal-${openPanelSelector}"]`);
   
                if (
                    buttonThatOpenedIt && !buttonThatOpenedIt.contains(event.target) &&
                    portalDropdown && !portalDropdown.contains(event.target)
                ) {
                    setOpenPanelSelector(null);
                }
            }
        };
   
        if (openPanelSelector) {
            document.addEventListener('mousedown', handleClickOutside);
        }
   
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openPanelSelector]);

    const handleParameterFilterChange = (parameter, value) => {
        setSelectedTemplateId(null);
        setTemporaryParameterFilters(prev => ({
            ...prev,
            [parameter]: value
        }));
    };

    // --- Effect to reset counts when filters/search change ---
    useEffect(() => {
        // Reset counts and scroll for the active tab when search or primary filter changes
        setRenderedCounts(prev => ({ ...prev, [activeTab]: ITEMS_PER_LOAD }));
         if (contentRef.current) contentRef.current.scrollTop = 0;
        setExpandedItems({}); // Collapse items on filter/search change
    }, [searchTerm, selectedFilter, appliedParameterFilters, selectedGenes, activeTab]); // Added activeTab


    // --- Scroll Handling for Incremental Rendering ---
    const handleScroll = useCallback(() => {
        if (!contentRef.current || isLoadingMore || isLoading) return;

        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        let currentFilteredData;
        let totalItemCount;

        switch (activeTab) {
            case 'genome':
                currentFilteredData = filteredGenomeData;
                break;
            case 'drugs':
                currentFilteredData = filteredDrugData;
                break;
            case 'traits':
                currentFilteredData = filteredTraitsData;
                break;
            default:
                return;
        }
        totalItemCount = currentFilteredData.length;


        // Check if we need to load more for the active tab
        if (renderedCounts[activeTab] < totalItemCount && scrollHeight - scrollTop <= clientHeight + SCROLL_THRESHOLD) {
            setIsLoadingMore(true);
            // Use a timeout to simulate network latency for loading more, can be adjusted/removed
            setTimeout(() => {
                setRenderedCounts(prev => ({
                    ...prev,
                    [activeTab]: Math.min(prev[activeTab] + ITEMS_PER_LOAD, totalItemCount) // Load more, but not beyond total
                }));
                setIsLoadingMore(false);
            }, 50); // Short delay to allow UI feedback if needed
        }
    }, [activeTab, renderedCounts, isLoadingMore, isLoading, filteredGenomeData, filteredDrugData, filteredTraitsData]); // Dependencies for scroll handler

    // Attach scroll listener
    useEffect(() => {
        const contentElement = contentRef.current;
        if (contentElement) {
            contentElement.addEventListener('scroll', handleScroll);
            return () => contentElement.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]); // Re-attach if handleScroll changes


    // --- UI Rendering ---

    // Render the parameter filters UI (Memoized for performance)
    const renderParameterFilters = useMemo(() => {
        const categoryOptions = {
            ClinVar_CLNSIG: ['Pathogenic', 'Likely_pathogenic', 'Conflicting_classifications_of_pathogenicity'],
            ReviewStar: ['0', '1', '2', '3', '4'],
            IMPACT: ['HIGH', 'MODERATE', 'MODIFIER'],
            am_class: ['likely_benign', 'likely_pathogenic', 'ambiguous']
        };

        const renderCategoricalFilter = (name, options) => (
            <div className="parameter-filter" key={name}>
                <div className="parameter-filter-title">{name.replace(/_/g, ' ')}</div>
                <div className="parameter-filter-options">
                    {options.map(option => (
                        <div className="parameter-option" key={option}>
                            <input
                                type="checkbox"
                                id={`${name}-${option}`}
                                checked={temporaryParameterFilters[name]?.includes(option) || false}
                                onChange={(e) => {
                                    const currentValues = temporaryParameterFilters[name] || [];
                                    const newValues = e.target.checked
                                        ? [...currentValues, option]
                                        : currentValues.filter(v => v !== option);
                                    handleParameterFilterChange(name, newValues.length > 0 ? newValues : undefined); // Use undefined to clear
                                }}
                            />
                            <label htmlFor={`${name}-${option}`}>{option.replace(/_/g, ' ')}</label>
                        </div>
                    ))}
                </div>
            </div>
        );

        const renderNumericFilter = (name, defaultRange) => {
             const currentFilterValue = temporaryParameterFilters[name];
             // Use temporary filter value if set, otherwise use default from parameterFilters state
            const currentMin = currentFilterValue?.min ?? defaultRange.min;
            const currentMax = currentFilterValue?.max ?? defaultRange.max;
             const step = (defaultRange.max - defaultRange.min) / 100 || 0.01; // Calculate step or use default

            return (
                <div className="parameter-filter" key={name}>
                    <div className="parameter-filter-title">{name.replace(/_/g, ' ')}</div>
                    <div className="parameter-filter-range">
                        <label htmlFor={`${name}-min`}>Min: {Number(currentMin).toFixed(2)}</label>
                        <input
                             id={`${name}-min`}
                            type="range"
                            min={defaultRange.min}
                            max={defaultRange.max}
                            step={step}
                            value={currentMin}
                            onChange={(e) => {
                                const newMin = parseFloat(e.target.value);
                                // Ensure min doesn't exceed max
                                handleParameterFilterChange(name, { min: newMin, max: Math.max(newMin, currentMax) });
                            }}
                        />
                    </div>
                    <div className="parameter-filter-range">
                         <label htmlFor={`${name}-max`}>Max: {Number(currentMax).toFixed(2)}</label>
                        <input
                            id={`${name}-max`}
                            type="range"
                            min={defaultRange.min}
                            max={defaultRange.max}
                            step={step}
                            value={currentMax}
                            onChange={(e) => {
                                const newMax = parseFloat(e.target.value);
                                // Ensure max doesn't go below min
                                handleParameterFilterChange(name, { min: Math.min(currentMin, newMax), max: newMax });
                            }}
                        />
                    </div>
                </div>
            );
        };

        return (
            <div className="parameter-filters-container">
                <h4>Categorical Filters</h4>
                {Object.entries(categoryOptions).map(([name, options]) =>
                    renderCategoricalFilter(name, options)
                )}

                <h4>Numerical Filters</h4>
                {renderNumericFilter('SZAreportCategory', parameterFilters.SZAreportCategory)}
                {renderNumericFilter('MAX_AF', parameterFilters.MAX_AF)}
                {renderNumericFilter('ada_score', parameterFilters.ada_score)}
                {renderNumericFilter('rf_score', parameterFilters.rf_score)}
                {renderNumericFilter('SpliceAI_pred_DS_AL', parameterFilters.SpliceAI_pred_DS_AL)}
                {renderNumericFilter('SpliceAI_pred_DS_DG', parameterFilters.SpliceAI_pred_DS_DG)}
                {renderNumericFilter('SpliceAI_pred_DS_DL', parameterFilters.SpliceAI_pred_DS_DL)}
            </div>
        );
    }, [temporaryParameterFilters, parameterFilters]); // Re-render only when temp filters change

    // Render the applied filters display (Memoized)
    const handleRemoveFilter = (key) => {
        // Clear any selected template when filters change manually
        setSelectedTemplateId(null);
    
        if (key === 'genes') {
            setTemporarySelectedGenes([]);
        } else {
            setTemporaryParameterFilters(prev => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        }
    
        // If no filters remain, reset locked state
        if (
            temporarySelectedGenes.length === 0 &&
            Object.keys(temporaryParameterFilters).length <= 1
        ) {
            setIsFilterLocked(false);
            setTemporaryFilterActive(false);
            setSelectedFilter('Whole Genome');
        }
    };
    
      const renderAppliedFilters = useMemo(() => {
        const chips = [];
      
        if (temporarySelectedGenes.length > 0) {
          chips.push(
            <button
              key="genes"
              className="applied-filter"
              onClick={() => handleRemoveFilter('genes')}
              style={{ cursor: 'pointer' }}
              title="Remove gene filter"
            >
              Genes: {temporarySelectedGenes.length} ×
            </button>
          );
        }
      
        Object.entries(temporaryParameterFilters).forEach(([param, value]) => {
          if (Array.isArray(value) && value.length > 0) {
            chips.push(
              <button
                key={param}
                className="applied-filter"
                onClick={() => handleRemoveFilter(param)}
                style={{ cursor: 'pointer' }}
                title={`Remove ${param} filter`}
              >
                {param.replace(/_/g, ' ')}: {value.join(', ').replace(/_/g, ' ')} ×
              </button>
            );
          } else if (
            typeof value === 'object' &&
            value !== null &&
            typeof value.min !== 'undefined'
          ) {
            chips.push(
              <button
                key={param}
                className="applied-filter"
                onClick={() => handleRemoveFilter(param)}
                style={{ cursor: 'pointer' }}
                title={`Remove ${param} filter`}
              >
                {param.replace(/_/g, ' ')}: {value.min.toFixed(2)}–{value.max.toFixed(2)} ×
              </button>
            );
          }
        });
      
        return <div className="applied-filters-container">{chips}</div>;
      }, [temporarySelectedGenes, temporaryParameterFilters]);
      
    // Memoized gene list for the popup
     const memoizedRenderGeneList = useMemo(() => {
         // Extract unique genes from the full variant report once
         const allGenes = [
             ...new Set(
                 reportData.variantReport
                     .flatMap(disease => disease.Genes ? disease.Genes.split(',').map(g => g.trim()) : [])
                     .filter(gene => gene) // Filter out empty strings
             )
         ];

         // Filter genes by search term first
         const searchTermLower = geneSearchTerm.toLowerCase();
         const filteredGenes = geneSearchTerm
             ? allGenes.filter(gene => gene.toLowerCase().includes(searchTermLower))
             : allGenes;

         // Sort genes: selected first, then alphabetically
         const sortedGenes = filteredGenes.sort((a, b) => {
             const aSelected = temporarySelectedGenes.includes(a);
             const bSelected = temporarySelectedGenes.includes(b);
             if (aSelected && !bSelected) return -1;
             if (!aSelected && bSelected) return 1;
             return a.localeCompare(b); // Alphabetical sort for same selection status
         });

         return (
             <>
                 {sortedGenes.map((gene) => (
     <button
     key={gene}
     className={`gene-item ${temporarySelectedGenes.includes(gene) ? 'selected' : ''}`}
     onClick={() => {
            // clear template highlight on any manual gene toggle
            setSelectedTemplateId(null);
            setTemporarySelectedGenes(prev =>
                prev.includes(gene) ? prev.filter(g => g !== gene) : [...prev, gene]
            );
        }}
 >
                         {temporarySelectedGenes.includes(gene) ? (
                             <FontAwesomeIcon icon={faCheck} className="gene-icon" />
                         ) : (
                             <span className="gene-icon">+</span>
                         )}
                         {gene}
                     </button>
                 ))}
                 {sortedGenes.length === 0 && geneSearchTerm && <p></p>}
                 {sortedGenes.length === 0 && !geneSearchTerm && <p>No genes available in the report.</p>}
             </>
         );
     }, [reportData.variantReport, geneSearchTerm, temporarySelectedGenes]); // Dependencies


    // --- Content Rendering Functions (Now use sliced data) ---

    const renderGenomicVariants = () => {
        const variantsToRender = filteredGenomeData.slice(0, renderedCounts.genome);

        if (filteredGenomeData.length === 0 && !isLoading) {
             return <p className="omicsview-genomics-no-data">No genomic variants match the current filters.</p>;
        }

        return (
            <div className="omicsview-genomics-list">
                {variantsToRender.map((variant, index) => {
                    const itemId = `genome-${variant.POS}-${variant.REF}-${variant.ALT}-${index}`; // More stable key
                    const isExpanded = !!expandedItems[itemId];
                    const isVisible = !(variant.hasOwnProperty('REPORT_TAG') && variant.REPORT_TAG === 0);

                    return (
                        <div key={itemId} className="omicsview-genomics-item">
                             <div
                                className="omicsview-genomics-item-header"
                                onClick={() => toggleExpand(itemId)}
                                style={{ opacity: isExpanded ? 1 : (isVisible ? 1 : 0.5) }}
                             >
                                <div className="omicsview-genomics-item-name">
                                    <FontAwesomeIcon icon={isVisible ? faEye : faEyeSlash} style={{ marginRight: '0.75vh'}} />
                                   {variant.Disease || `Variant ${index}`}  ({variant.SYMBOL || variant.Genes?.split(',')[0] || 'Unknown Gene'})
                                </div>
                                 <div className="omicsview-genomics-item-info">
                                      <span>{variant.IMPACT || 'Unknown'}</span>
                                      <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} style={{ marginLeft: '10px' }}/>
                                 </div>
                             </div>
                             {/* FIX: Removed conditional rendering wrapper {isExpanded && (...)} */}
                             <div className={`omicsview-genomics-item-content ${isExpanded ? 'open' : ''}`}>
                                 {/* Content is always rendered structurally, CSS handles animation */}
                                 <>
                                     <div className="omicsview-genomics-item-detail">
                                         <div className="omicsview-genomics-item-title">Gene(s)</div>
                                         <div className="omicsview-genomics-item-value">{variant.Genes || 'N/A'}</div>
                                     </div>
                                     <div className="omicsview-genomics-grid">
                                          {/* Simplified Grid - Add more fields as needed */}
                                         <div className="omicsview-genomics-grid-item"><div className="omicsview-genomics-item-title">Position</div><div className="omicsview-genomics-item-value">{variant.POS || 'N/A'}</div></div>
                                         <div className="omicsview-genomics-grid-item"><div className="omicsview-genomics-item-title">HGVS</div><div className="omicsview-genomics-item-value">{variant.HGVSc || '?'}</div></div>
                                         <div className="omicsview-genomics-grid-item"><div className="omicsview-genomics-item-title">rsID</div><div className="omicsview-genomics-item-value">{variant.Existing_variation || '?'}</div></div>
                                         <div className="omicsview-genomics-grid-item"><div className="omicsview-genomics-item-title">Impact</div><div className="omicsview-genomics-item-value">{variant.IMPACT || 'N/A'}</div></div>
                                         <div className="omicsview-genomics-grid-item"><div className="omicsview-genomics-item-title">ClinVar</div><div className="omicsview-genomics-item-value">{(variant.ClinVar_CLNSIG || 'N/A').replace(/_/g, ' ')}</div></div>
                                         
                                         <div className="omicsview-genomics-grid-item"><div className="omicsview-genomics-item-title">Review Star</div><div className="omicsview-genomics-item-value">{variant.ReviewStar || 'N/A'}</div></div>
                                         <div className="omicsview-genomics-grid-item"><div className="omicsview-genomics-item-title">ACMG Classification</div><div className="omicsview-genomics-item-value">{(variant.acmg_classification || 'N/A').replace(/_/g, ' ')}</div></div>
                                         <div className="omicsview-genomics-grid-item"><div className="omicsview-genomics-item-title">SZA Report Category</div><div className="omicsview-genomics-item-value">{(variant.SZAreportCategory || 'N/A').replace(/_/g, ' ')}</div></div>

                                     </div>
                                     {variant.DiseaseInfo && (
                                         <div className="omicsview-genomics-item-detail">
                                             <div className="omicsview-genomics-item-title">Disease Info</div>
                                             <div className="omicsview-genomics-item-value">{variant.DiseaseInfo}</div>
                                         </div>
                                     )}
               
                                     <div className="omicsview-below-genomics-grid-item">
                                        <div className="omicsview-genomics-item-button-container">                           <button
                                             className="omicsview-genomics-item-button"
                                             onClick={() => {
                                                 setModalType('variant');
                                                 setModalData(variant);
                                                     setModalVisible(true);
                                             }}
                                         >
                                             Additional Information
                                         </button>
                                         {variant.hpoMatches && variant.hpoMatches.length > 0 && (
                                   <button
                                   className="omicsview-genomics-item-button"
                               onClick={() => {
                                   setModalType('hpo');
                                   setModalData(variant.hpoMatches || []);
                                   setModalVisible(true);
                               }}
                               >
                               HPO Findings
                           </button>
                           
                                            )}

{/* NEW JSX for the Clinician Panel Selector Dropdown */}
{/* NEW JSX for the Clinician Panel Selector Dropdown using Portal */}
<div className="genomics-variant-panel-selector-container">
    {(() => {
        const uniqueVariantKey = `variant-panel-${variant.Genes}-${variant.Disease}-${variant.index || index}`;
        const isCurrentSelectorOpen = openPanelSelector === uniqueVariantKey;
        const selectedCount = variant.ClinC?.selections?.length || 0;
        const buttonText = selectedCount > 0 ? `${selectedCount} Tagged` : "Tag As";
        
        // Create a ref for the button itself for positioning the portal
        const buttonRef = React.createRef(); // Use React.createRef inside the map's scope

        const handleSelectorToggle = (e) => {
            e.stopPropagation();
            if (isCurrentSelectorOpen) {
                setOpenPanelSelector(null);
            } else {
                if (buttonRef.current) {
                    const rect = buttonRef.current.getBoundingClientRect();
                    // To position above, we need the dropdown's height.
                    // This is tricky without rendering it first to measure.
                    // A common approach is to estimate or set a fixed/max-height on the dropdown.
                    // Let's assume a fixed height for simplicity here, or you can try to get it dynamically.
                    // For dynamic height, you'd render it hidden, measure, then show.

                    // Simple approach: Position based on button's top, then transform Y.
                    // More robust: Calculate based on an estimated/actual dropdown height.
                    // Let's try to get the dropdown's height after it's potentially rendered (for next click)
                    // or use a fixed/max height. For this minimal change, we'll use a transform.

                    // For now, let's just try to place it relative to the button's top
                    // and adjust with a small negative margin in CSS or a transform.
                    // The most reliable way is to calculate the dropdown's actual height.

                    // Get the dropdown element (it might not exist on the first open if not rendered yet)
                    const dropdownElement = document.getElementById(`panel-dropdown-portal-${uniqueVariantKey}`);
                    let dropdownHeight = 300; // Default/max height from your CSS
                    if (isCurrentSelectorOpen && dropdownElement) { // If it was open and we are re-calculating
                         // This won't work on the very first open as it's not rendered yet to measure.
                         // For simplicity, we'll use the max-height as an estimate.
                        // dropdownHeight = dropdownElement.offsetHeight;
                    }


                    setPanelDropdownPosition({
                        // top: rect.top + window.scrollY - dropdownHeight - 5, // Position above, minus dropdown height & gap
                        // OR, using bottom to align bottom of dropdown with top of button
                        top: rect.top + window.scrollY - 5, // This will be the top of the dropdown, we then transform it
                        left: rect.left + window.scrollX,
                        transform: `translateY(-100%)`, // Move it up by its own height
                    });
                }
                setOpenPanelSelector(uniqueVariantKey);
            }
        };
        
        const handleCheckboxChangeAndStopPropagation = (e, panelName) => {
            e.stopPropagation();
            handleClinCPanelToggle(variant, panelName);
        };
        
        const handleLabelClickAndStopPropagation = (e, panelName) => {
            e.stopPropagation();
            handleClinCPanelToggle(variant, panelName);
        };

        return (
            <div className="clinician-panel-selector-wrapper-inline" ref={buttonRef}>
                <button
                    className={`clinician-panel-selector-button-inline ${isCurrentSelectorOpen ? 'open' : ''} ${selectedCount > 0 ? 'selected' : ''}`}
                    onClick={handleSelectorToggle}
                    aria-expanded={isCurrentSelectorOpen}
                    aria-controls={`panel-dropdown-portal-${uniqueVariantKey}`}
                >
                    {buttonText}
                    <FontAwesomeIcon icon={isCurrentSelectorOpen ? faChevronUp : faChevronDown} className="panel-selector-icon-inline" />
                </button>
                {isCurrentSelectorOpen && ReactDOM.createPortal(
                    <div 
                    className="clinician-panel-dropdown-inline clinician-panel-dropdown-portal" 
                    id={`panel-dropdown-portal-${uniqueVariantKey}`}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        position: 'fixed',
                        top: `${panelDropdownPosition.top}px`,
                        left: `${panelDropdownPosition.left}px`,
                        transform: panelDropdownPosition.transform || 'none', // Apply the transform
                        zIndex: 1050, 
                    }}
                >
                        {CLINICIAN_PANEL_OPTIONS.map(panelGroup => (
                            <div key={panelGroup.group} className="panel-group-inline">
                                    <div className="panel-group-title-inline">{panelGroup.group}</div>
                                {panelGroup.options.map(panelName => {
                                    const uniqueId = `clinc-panel-item-portal-${uniqueVariantKey}-${panelName.replace(/\s+/g, '-').replace(/[()]/g, '')}`;
                                    const isSelected = Array.isArray(variant.ClinC?.selections) && variant.ClinC.selections.includes(panelName);
                                    return (
                                        <div 
                                        key={panelName} 
                                        className="panel-option-inline"
                                        onClick={(e) => {
                                            // Prevent clicks on the checkbox itself from re-triggering this
                                            if (e.target.type !== 'checkbox') {
                                                handleClinCPanelToggle(variant, panelName);
                                            }
                                        }}
                                        style={{ cursor: 'pointer' }} // Explicitly set cursor for the whole div
                                    >
                                        <input
                                            type="checkbox"
                                            id={uniqueId}
                                            checked={isSelected}
                                            onChange={(e) => { 
                                                // This onChange will be triggered by clicks on the checkbox OR the label (due to htmlFor)
                                                // We can let this directly call the toggle function.
                                                // The div's onClick will handle clicks on the div's padding area.
                                                e.stopPropagation(); // Stop propagation if the checkbox itself handles the change
                                                handleClinCPanelToggle(variant, panelName);
                                            }}
                                            onClick={(e) => e.stopPropagation()} // Also stop propagation for direct checkbox clicks
                                            className="panel-checkbox-inline"
                                        />
                                        <label
                                            htmlFor={uniqueId} // This makes the label click toggle the checkbox
                                            className="panel-label-inline"
                                            onClick={(e) => e.stopPropagation()} // Stop propagation to prevent div's onClick if label is directly clicked
                                        >
                                            {panelName}
                                        </label>
                                    </div>
                                    );
                                })}
                                {panelGroup.group !== "Other" && CLINICIAN_PANEL_OPTIONS.indexOf(panelGroup) < CLINICIAN_PANEL_OPTIONS.length - (CLINICIAN_PANEL_OPTIONS.some(pg => pg.group === "Other") ? 1 : 1) && (
                                    <hr className="panel-separator-inline" />
                                )}
                            </div>
                        ))}
                    </div>,
                    document.body // Target for the portal
                )}
            </div>
        );
    })()}
</div>
                                            
                                            
                                            
                                            
                                            </div>
              
                               
                                         {variant.hasOwnProperty('REPORT_TAG') && (
                                             <div className="omicsview-genomics-item-detail">
                                                 <label className="omicsview-genomics-toggle-switch">
                                                     <input type="checkbox" checked={isVisible} onChange={(e) => handleToggleFilter(e, variant, 'Disease', e.target.checked)} />
                                                     <span className="omicsview-genomics-toggle-slider"></span>
                                                 </label>
                                             </div>
                                         )}
                                         
                                     </div>
                                 </>
                            </div>
                        </div>
                    );
                })}
                {isLoadingMore && activeTab === 'genome' && <div className="omicsview-genomics-loading-more"><FontAwesomeIcon icon={faSpinner} spin /> </div>}
                 {renderedCounts.genome < filteredGenomeData.length && !isLoadingMore && activeTab === 'genome' &&
                     <div onClick={handleScroll} className="omicsview-genomics-load-more-sentinel"></div> // Use a sentinel div instead of a button for auto-load
                 }
            </div>
        );
    };

    const renderDrugData = () => {
        const drugsToRender = filteredDrugData.slice(0, renderedCounts.drugs);

        if (filteredDrugData.length === 0 && !isLoading) {
            return <p className="omicsview-genomics-no-data">No drug data matches the current filters.</p>;
        }

        return (
            <div className="omicsview-genomics-list">
                {drugsToRender.map((drug, index) => {
                    const itemId = `drugs-${drug.Gene}-${drug['Drug.s.']}-${index}`; // More stable key
                    const isExpanded = !!expandedItems[itemId];
                    const isVisible = !(drug.hasOwnProperty('REPORT_TAG') && drug.REPORT_TAG === 0);

                    return (
                        <div key={itemId} className="omicsview-genomics-item">
                             <div
                                 className="omicsview-genomics-item-header"
                                 onClick={() => toggleExpand(itemId)}
                                 style={{ opacity: isExpanded ? 1 : (isVisible ? 1 : 0.5) }}
                             >
                                <div className="omicsview-genomics-item-name">
                                    <FontAwesomeIcon icon={isVisible ? faEye : faEyeSlash} style={{ marginRight: '0.75vh'}} />
                                    {drug['Drug.s.'] || `Drug ${index}`} ({drug.Gene || 'N/A'})
                                </div>
                                <div className="omicsview-genomics-item-info">
                                     <span>{drug['Phenotype.s.'] || 'N/A'}</span>
                                     <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} style={{ marginLeft: '10px' }}/>
                                </div>
                            </div>
                            {/* FIX: Removed conditional rendering wrapper {isExpanded && (...)} */}
                             <div className={`omicsview-genomics-item-content ${isExpanded ? 'open' : ''}`}>
                                 {/* Content is always rendered structurally, CSS handles animation */}
                                 <>
                                     <div className="omicsview-genomics-item-detail">
                                         <div className="omicsview-genomics-item-title">Genotype:</div>
                                         <div className="omicsview-genomics-item-value">{drug.Genotype || 'N/A'}</div>
                                     </div>
                                     <div className="omicsview-genomics-grid">
                                         <div className="omicsview-genomics-grid-item"><div className="omicsview-genomics-item-title">Zygosity</div><div className="omicsview-genomics-item-value">{drug.Zygosity || 'N/A'}</div></div>
                                         <div className="omicsview-genomics-grid-item"><div className="omicsview-genomics-item-title">Phenotype</div><div className="omicsview-genomics-item-value">{drug['Phenotype.s.'] || 'N/A'}</div></div>
                                         <div className="omicsview-genomics-grid-item"><div className="omicsview-genomics-item-title">Gene</div><div className="omicsview-genomics-item-value">{drug.Gene || 'N/A'}</div></div>
                                         <div className="omicsview-genomics-grid-item"><div className="omicsview-genomics-item-title">Evidence Lvl</div><div className="omicsview-genomics-item-value">{drug['Level.of.Evidence'] || 'N/A'}</div></div>
                                     </div>
                                     <div className="omicsview-below-genomics-grid-item">
                                         <div className="omicsview-genomics-item-button">Additional Information</div>
                                         {drug.hasOwnProperty('REPORT_TAG') && (
                                             <div className="omicsview-genomics-item-detail">
                                                 <label className="omicsview-genomics-toggle-switch">
                                                     <input type="checkbox" checked={isVisible} onChange={(e) => handleToggleFilter(e, drug, 'Drug', e.target.checked)}/>
                                                     <span className="omicsview-genomics-toggle-slider"></span>
                                                 </label>
                                             </div>
                                         )}
                                     </div>
                                 </>
                            </div>
                        </div>
                    );
                })}
                 {isLoadingMore && activeTab === 'drugs' && <div className="omicsview-genomics-loading-more"><FontAwesomeIcon icon={faSpinner} spin /> </div>}
                  {renderedCounts.drugs < filteredDrugData.length && !isLoadingMore && activeTab === 'drugs' &&
                     <div onClick={handleScroll} className="omicsview-genomics-load-more-sentinel"></div>
                 }
            </div>
        );
    };

    const renderTraitsData = () => {
        const traitsToRender = filteredTraitsData.slice(0, renderedCounts.traits);

        if (filteredTraitsData.length === 0 && !isLoading) {
             return <p className="omicsview-genomics-no-data">No traits match the current filters.</p>;
        }

        return (
            <div className="omicsview-genomics-list">
                {traitsToRender.map((trait, index) => {
                    const itemId = `traits-${trait.category}-${trait.genes}-${index}`; // More stable key
                    const isExpanded = !!expandedItems[itemId];
                    const isVisible = !(trait.hasOwnProperty('REPORT_TAG') && trait.REPORT_TAG === 0);

                    return (
                        <div key={itemId} className="omicsview-genomics-item">
                             <div
                                 className="omicsview-genomics-item-header"
                                 onClick={() => toggleExpand(itemId)}
                                 style={{ opacity: isExpanded ? 1 : (isVisible ? 1 : 0.5) }}
                            >
                                <div className="omicsview-genomics-item-name">
                                     <FontAwesomeIcon icon={isVisible ? faEye : faEyeSlash} style={{ marginRight: '0.75vh'}} />
                                    {trait.category || `Trait ${index}`} ({trait.genes || 'N/A'})
                                </div>
                                <div className="omicsview-genomics-item-info">
                                     <span>{trait.Zygosity || 'N/A'}</span>
                                     <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} style={{ marginLeft: '10px' }}/>
                                </div>
                            </div>
                             {/* FIX: Removed conditional rendering wrapper {isExpanded && (...)} */}
                             <div className={`omicsview-genomics-item-content ${isExpanded ? 'open' : ''}`}>
                                 {/* Content is always rendered structurally, CSS handles animation */}
                                 <>
                                     <div className="omicsview-genomics-item-detail">
                                         <div className="omicsview-genomics-item-title">Description:</div>
                                         <div className="omicsview-genomics-item-value">{trait.Description || 'N/A'}</div>
                                     </div>
                                     <div className="omicsview-genomics-grid">
                                         <div className="omicsview-genomics-grid-item"><div className="omicsview-genomics-item-title">Patient Genotype</div><div className="omicsview-genomics-item-value">{trait['Patient_genotypes'] || 'N/A'}</div></div>
                                         <div className="omicsview-genomics-grid-item"><div className="omicsview-genomics-item-title">Genes</div><div className="omicsview-genomics-item-value">{trait.genes || 'N/A'}</div></div>
                                         <div className="omicsview-genomics-grid-item"><div className="omicsview-genomics-item-title">Variants</div><div className="omicsview-genomics-item-value">{trait.variants || 'N/A'}</div></div>
                                         <div className="omicsview-genomics-grid-item"><div className="omicsview-genomics-item-title">Zygosity</div><div className="omicsview-genomics-item-value">{trait.Zygosity || 'N/A'}</div></div>
                                     </div>
                                     <div className="omicsview-below-genomics-grid-item">
                                         <div className="omicsview-genomics-item-button">Additional Information</div>
                                         {trait.hasOwnProperty('REPORT_TAG') && (
                                             <div className="omicsview-genomics-item-detail">
                                                 <label className="omicsview-genomics-toggle-switch">
                                                     <input type="checkbox" checked={isVisible} onChange={(e) => handleToggleFilter(e, trait, 'Trait', e.target.checked)}/>
                                                     <span className="omicsview-genomics-toggle-slider"></span>
                                                 </label>
                                             </div>
                                         )}
                                     </div>
                                 </>
                            </div>
                        </div>
                    );
                })}
                 {isLoadingMore && activeTab === 'traits' && <div className="omicsview-genomics-loading-more"><FontAwesomeIcon icon={faSpinner} spin /> </div>}
                  {renderedCounts.traits < filteredTraitsData.length && !isLoadingMore && activeTab === 'traits' &&
                     <div onClick={handleScroll} className="omicsview-genomics-load-more-sentinel"></div>
                 }
            </div>
        );
    };

    // Handle tab change
    const handleTabChange = (tab) => {
        if (tab === activeTab) return; // Do nothing if clicking the current tab
        setActiveTab(tab);
        setExpandedItems({}); // Collapse all items on tab change
        // Reset scroll position for the new tab
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
         // Reset rendered count for the *new* tab only if needed (already handled by useEffect dependency)
         // setRenderedCounts(prev => ({ ...prev, [tab]: ITEMS_PER_LOAD }));
    };

    // Determine which content to display based on active tab
    const renderActiveContent = () => {
        switch (activeTab) {
            case 'genome': return renderGenomicVariants();
            case 'drugs': return renderDrugData();
            case 'traits': return renderTraitsData();
            default: return <p>Select a tab to view data</p>;
        }
    };

    // --- CSV Download ---
    const handleGenomicsDownload = () => {
        if (isLoading || !reportData || typeof reportData !== 'object') {
            alert("Genomics data is still loading or not available.");
            return;
        }

        const dataSources = [
            { name: "Variant_Report", data: reportData.variantReport },
            { name: "Drug_Data", data: reportData.drugData },
            { name: "Traits", data: reportData.traits }
        ];

        const csvFiles = dataSources
            .filter(source => source.data && source.data.length > 0) // Only include sources with data
            .map(source => {
                // Ensure data is an array before trying to access [0]
                 if (!Array.isArray(source.data) || source.data.length === 0) {
                     return null; // Skip if data is not a non-empty array
                 }
                const headers = Object.keys(source.data[0]);
                const rows = source.data.map(row =>
                    headers.map(header => formatValueForCSV(row[header])).join(',')
                );
                const csvContent = [headers.join(','), ...rows].join('\r\n');
                return { name: source.name, content: csvContent };
            }).filter(file => file !== null); // Remove null entries

        if (csvFiles.length === 0) {
            alert("No genomics data available to download.");
            return;
        }

        downloadZipWithCSVs(csvFiles, `genomics_report_${id || data.patientId || 'data'}.zip`);
    };

    const formatValueForCSV = (value) => {
        if (value === null || typeof value === 'undefined') return '';
        let stringValue = String(value);
        stringValue = stringValue.replace(/"/g, '""'); // Escape double quotes
        if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
            stringValue = `"${stringValue}"`; // Enclose in quotes if needed
        }
        return stringValue;
    };

    const downloadZipWithCSVs = (csvFiles, zipFileName) => {
        const jszipUrl = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
        // Check if JSZip is already loaded
        if (typeof window.JSZip === 'undefined') {
            const script = document.createElement('script');
            script.src = jszipUrl;
            script.async = true;
            script.onload = () => generateAndDownloadZip(csvFiles, zipFileName);
            script.onerror = () => alert('Failed to load JSZip library. Please check your internet connection or allow scripts from cdnjs.cloudflare.com.');
            document.head.appendChild(script);
        } else {
            generateAndDownloadZip(csvFiles, zipFileName);
        }
    };

    const generateAndDownloadZip = (csvFiles, zipFileName) => {
         try {
             const zip = new window.JSZip();
             csvFiles.forEach(file => zip.file(`${file.name}.csv`, file.content));

             zip.generateAsync({ type: 'blob' })
                 .then(content => {
                     const link = document.createElement('a');
                     link.href = URL.createObjectURL(content);
                     link.download = zipFileName;
                     document.body.appendChild(link);
                     link.click();
                     document.body.removeChild(link);
                     URL.revokeObjectURL(link.href);
                 })
                 .catch(error => {
                      console.error('Error generating ZIP file:', error);
                      alert('Failed to generate ZIP file.');
                 });
         } catch (error) {
              console.error('Error initializing JSZip or adding files:', error);
              alert('An error occurred during the ZIP creation process.');
         }
    };
// add these two helpers somewhere in your component, next to matchesTempFilters:
const hideSelectedBulk = async () => {
    const toHide = reportData.variantReport.filter(matchesTempFilters);
    if (!toHide.length) {
      setAdvancedMenuVisible(false);
      return;
    }
    await fetch('https://service1.szapfs.org/api/GenomicsToggleBulk', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sampleID: data.patientId,
        type: 'Disease',
        state: 0,
        items: toHide.map(i => ({ Genes: i.Genes, Disease: i.Disease }))
      })
    });
    setReportData(prev => ({
      ...prev,
      variantReport: prev.variantReport.map(v =>
        toHide.some(h => h.Genes === v.Genes && h.Disease === v.Disease)
          ? { ...v, REPORT_TAG: 0 }
          : v
      )
    }));
    setAdvancedMenuVisible(false);
  };
  
  const showOnlySelectedBulk = async () => {
    const matches = reportData.variantReport.filter(matchesTempFilters);
    const nonMatches = reportData.variantReport.filter(i => !matchesTempFilters(i));
    await Promise.all([
      fetch('https://service1.szapfs.org/api/GenomicsToggleBulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sampleID: data.patientId,
          type: 'Disease',
          state: 1,
          items: matches.map(i => ({ Genes: i.Genes, Disease: i.Disease }))
        })
      }),
      fetch('https://service1.szapfs.org/api/GenomicsToggleBulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sampleID: data.patientId,
          type: 'Disease',
          state: 0,
          items: nonMatches.map(i => ({ Genes: i.Genes, Disease: i.Disease }))
        })
      })
    ]);
    setReportData(prev => ({
      ...prev,
      variantReport: prev.variantReport.map(v => ({
        ...v,
        REPORT_TAG: matches.some(m => m.Genes === v.Genes && m.Disease === v.Disease) ? 1 : 0
      }))
    }));
    setAdvancedMenuVisible(false);
  };
  const resetVisibilityBulk = async () => {
    const allItems = reportData.variantReport;
    if (!allItems.length) {
      setAdvancedMenuVisible(false);
      return;
    }
    await fetch('https://service1.szapfs.org/api/GenomicsToggleBulk', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sampleID: data.patientId,
        type: 'Disease',
        state: 1,
        items: allItems.map(i => ({ Genes: i.Genes, Disease: i.Disease }))
      })
    });
    setReportData(prev => ({
      ...prev,
      variantReport: prev.variantReport.map(v => ({ ...v, REPORT_TAG: 1 }))
    }));
    setAdvancedMenuVisible(false);
  };
    // --- Main Component Return ---
    return (
        <div className="omicsview-genomics-container">
            {/* Header */}
            <div className="omicsview-genomics-search">
                <FontAwesomeIcon icon={faSearch} className="omicsview-genomics-search-icon" />
                <input
                    type="text"
                    placeholder={`Search ${activeTab}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="omicsview-genomics-search-input"
                />
                <button
                    className="omicsview-genomics-download-button"
                    onClick={handleGenomicsDownload}
                    disabled={isLoading || error || (!reportData.variantReport?.length && !reportData.drugData?.length && !reportData.traits?.length)}
                    title="Download All Genomics Data (ZIP)"
                >
                    <FontAwesomeIcon icon={faFileDownload} />
                </button>
            </div>

            {/* Tabs */}
            <div className="omicsview-genomics-filters">
                <div className="omicsview-genomics-tabs">
                    {['genome', 'traits', 'drugs'].map(tab => (
                         <button
                             key={tab}
                             className={`omicsview-genomics-tab ${activeTab === tab ? 'active' : ''}`}
                             onClick={() => handleTabChange(tab)}
                             disabled={isLoading} // Disable tabs while loading initial data
                         >
                             <FontAwesomeIcon icon={tab === 'genome' ? faDna : tab === 'traits' ? faHeartPulse : faPills} />
                             <span style={{ marginLeft: '0.5vh' }}>{tab.charAt(0).toUpperCase() + tab.slice(1)}</span>
                         </button>
                     ))}

                    <div style={{marginLeft: 'auto'}}> 
                      <ReportModule 
                        user={user} 
                        reportType={selectedFilter || 'Whole Genome'} 
                        preset={`${data.sampleId || id}`} 
                      />
                    </div>
                </div>
            </div>

            {/* Sub-filters (Only show for Genome tab for now) */}
            {activeTab === 'genome' && (
                 <div className="omicsview-genomics-filters-sub">
                     <div className="omicsview-genomics-tabs">
                         {['Whole Genome', 'Health Risks', 'Newborn', 'Carrier', 'Cancer'].map(filter => (
           <div
           key={filter}
           className={[
             "omicsview-genomics-filterpanel",
             selectedFilter === filter ? "active" : "",
             isFilterLocked ? "disabled" : ""
           ].join(" ")}
           onClick={() => {
             if (!isFilterLocked) {
               setSelectedFilter(filter);
               setSelectedGenes([]);
               setAppliedParameterFilters({});
               setTemporaryFilterActive(false);
             }
           }}
         >
           {filter}
           
         </div>
         
                         ))}
                     </div>
                     <div className="omicsview-genomics-filterbutton">
                        <FontAwesomeIcon
                            icon={faFilter}
                            onClick={togglePopup}
                            className={`${temporaryFilterActive || isFilterLocked ? 'active' : ''}`}
                        />
                    </div>
                 </div>
            )}


            {/* Filter Popup */}
            {popupVisible && (
                <div className="omicsview-genomics-popup-overlay" onClick={togglePopup}>
                    <div className="omicsview-genomics-popup" onClick={e => e.stopPropagation()}>
                        <div className="omicsview-genomics-popup__header">
                            {activeFilterTab === 'genes' ? (
                                <input
                                    type="text"
                                    placeholder="Search or Add Gene..."
                                    className="omicsview-genomics-popup__search-bar"
                                    value={geneSearchTerm}
                                    onChange={e => setGeneSearchTerm(e.target.value.toUpperCase())}
                                    autoFocus
                                />
                            ) : (
                                <div className="omicsview-genomics-popup__search-bar-placeholder">
                                    <input
                                        type="text"
                                        placeholder="New Template Name"
                                        value={newTemplateName}
                                        onChange={e => setNewTemplateName(e.target.value)}
                                        className={`omicsview-genomics-popup__search-bar ${nameError ? 'gen-error-border' : ''}`}
                                    />
                                    <button className="omicsview-genomics-popup__save-btn" onClick={handleSaveTemplate}>
                                        <FontAwesomeIcon icon={faSave}  /> 
                                    </button>
                                </div>
                            )}
                            <button className="omicsview-genomics-popup__close-btn" onClick={togglePopup}>×</button>
                        </div>

                        <div className="omicsview-genomics-popup__headerbelow">
                            <button
                                className={`omicsview-genomics-popup__headerbelow__button ${activeFilterTab === 'genes' ? 'active' : ''}`}
                                onClick={() => setActiveFilterTab('genes')}
                            >
                                Genes
                            </button>
                            <button
                                className={`omicsview-genomics-popup__headerbelow__button ${activeFilterTab === 'parameters' ? 'active' : ''}`}
                                onClick={() => setActiveFilterTab('parameters')}
                            >
                                Parameters
                            </button>
                            <button
                                className={`omicsview-genomics-popup__headerbelow__button ${activeFilterTab === 'templates' ? 'active' : ''}`}
                                onClick={() => setActiveFilterTab('templates')}
                            >
                                Templates
                            </button>
                        </div>

                        <div className="omicsview-genomics-popup__body">
                            {activeFilterTab === 'genes' && memoizedRenderGeneList}
                            {activeFilterTab === 'parameters' && renderParameterFilters}
                            {activeFilterTab === 'templates' && (
      <div className="genomics_template-manager">
      {templates.map(tpl => (
        <div
          key={tpl.id}
          className={`template-item${selectedTemplateId === tpl.id ? ' selected' : ''}`}
        >
          {renamingTemplateId === tpl.id ? (
            <>
                   <button
                className="genomics-pre-icon-button"
                onClick={() => {
                  confirmRename();
                  setSelectedTemplateId(null);
                }}
              >
                <FontAwesomeIcon icon={faCheck} />
              </button>
              <input
                className="template-input"
                value={renameValue}
                onChange={e => setRenameValue(e.target.value)}
              />
       
              <button
                className="genomics-pre-icon-button"
                onClick={() => {
                  setRenamingTemplateId(null);
                  setSelectedTemplateId(null);
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </>
          ) : (
            <>
              <button
                className="genomics-pre-icon-button"
                onClick={() => {
                  startRename(tpl);
                  setSelectedTemplateId(null);
                }}
              >
                <FontAwesomeIcon icon={faPen} />
              </button>
              <button
                className="template-name"
                onClick={() => {
                  applyTemplate(tpl);
                  setSelectedTemplateId(tpl.id);
                }}
              >
                {tpl.template_name}
              </button>
              <button
                className="genomics-pre-icon-button"
                onClick={() => {
                  deleteTemplate(tpl.id);
                  setSelectedTemplateId(null);
                }}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </>
          )}
        </div>
      ))}
      {templates.length === 0 && (
        <div className="no-templates">No templates saved.</div>
      )}
    </div>
    

                            )}
                        </div>

                        <div className="omicsview-genomics-applied-filters">
                            {renderAppliedFilters}
                        </div>

                        <div className="omicsview-genomics-popup__footer">
                            <button className="omicsview-genomics-clear-button" onClick={handleClearFilters}>
                                Clear All Filters
                            </button>
                            <button className="omicsview-genomics-apply-button" onClick={handleApplyGenes}>
                                Apply Filters
                            </button>
                            <div className="omicsview-genomics-advanced">
  <button
    className="omicsview-genomics-advanced-button"
    onClick={() => setAdvancedMenuVisible(v => !v)}
  >
    <FontAwesomeIcon icon={faGear} />
  </button>

  {advancedMenuVisible && (
  <div className="omicsview-genomics-advanced-dropdown">
    <button onClick={hideSelectedBulk}>
      Hide Selected
    </button>
    <button onClick={showOnlySelectedBulk}>
      Show Only Selected
    </button>
    <button onClick={resetVisibilityBulk}>
      Reset Visibility
    </button>
  </div>
)}

</div>

                        </div>
                    </div>
                </div>
            )}

            {/* Content Area (Scrollable) */}
            <div className="omicsview-genomics-content" ref={contentRef}>
                {isLoading ? (
                    <div className="omicsview-genomics-loading">
                         <FontAwesomeIcon icon={faSpinner} spin size="3x" />
                    </div>
                ) : error ? (
                    <div className="omicsview-genomics-error">
                        <h4>Error Loading Data</h4>
                        <p>{error}</p>
                        <p>Patient ID: {data.patientId || id || 'N/A'}</p>
                    </div>
                ) : (
                    renderActiveContent() // Render content based on active tab
                )}
            </div>
            {modalVisible && ReactDOM.createPortal(
  <div className="genomics-additive-modal__overlay">
    <div className="genomics-additive-modal__content">
      <div className="genomics-additive-modal__header">
        <h3 className="genomics-additive-modal__title">
          {modalType === 'variant' ? 'Additional Information' : 'HPO Findings'}
        </h3>
        <button
          className="genomics-additive-modal__close"
          onClick={() => setModalVisible(false)}
        >
          ×
        </button>
      </div>

      <div className="genomics-additive-modal__grid">
      {modalType === 'variant'
  ? Object.entries(modalData)
      .filter(([key]) => key !== 'hpoMatches')
      .sort(([, a], [, b]) => {
        const sa = typeof a === 'object' ? JSON.stringify(a) : String(a);
        const sb = typeof b === 'object' ? JSON.stringify(b) : String(b);
        return sa.length - sb.length; // shortest values first, longest last
      })
      .map(([key, value]) => (
        <div className="genomics-additive-modal__cell" key={key}>
<span className="genomics-additive-modal__label">
  {key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')}
</span>
          <span className="genomics-additive-modal__value">
            {value == null
              ? ''
              : typeof value === 'object'
                ? JSON.stringify(value, null, 2)
                : value}
          </span>
        </div>
      ))
          : modalData.map((hpo, idx) => (
              <div className="genomics-additive-modal__row" key={idx}>
                {Object.entries(hpo).map(([k, v]) => (
                  <div className="genomics-additive-modal__cell" key={k}>
<span className="genomics-additive-modal__label">
  {k
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')}
</span>
                    <span className="genomics-additive-modal__value">
                      {v == null
                        ? '—'
                        : typeof v === 'object'
                          ? JSON.stringify(v, null, 2)
                          : v}
                    </span>
                  </div>
                ))}
              </div>
            ))}
      </div>
    </div>
  </div>,
  document.body
)}




        </div>
    );
};

export default GenomicsReport;